import {Injectable} from '@angular/core';
import {ApiClientService} from './api-client.service';
import {DailyReport} from '../types/data.types';
import {UrlService} from './url.service';
import {EmptyRowService} from './empty-row.service';
import {ConstantsService} from './constants.service';
import {AccountPermissionsService} from './account-permissions.service';
import {filter, tap} from 'rxjs/operators';
import {createInitialNetworkEntity, resolveNetworkEntity} from './helper-functions';

declare const toastr: any;
declare const swal: any;
declare const $: any;
declare const moment: any;

@Injectable()
export class DailyReportsService {
    showDetailedView = false;
    isDailyReportsModalShow = false;
    addEntryModal = false;
    showDailyEntriesModal = false;

    selectedItemData: DailyReport;

    dailyReportInDailyReportsModal: DailyReport;

    data = {
        allDailyReports: {
            get: '/v1/daily_report.json?expand=building,suite,resident,createdBy,entries,entries.createdBy',
            data: [],
            total: null
        },
    };
    tabId = 'allDailyReports';
    spinnerShow = true;
    showServiceUnavailable = false;
    buildings: Array<any> = [];
    buildingsForRead: Array<any> = [];
    buildingsForUpdate: Array<any> = [];
    serviceName: string = 'daily_report';
    entryTyped = false;

    constructor(
        public api: ApiClientService,
        public urlService: UrlService,
        public emptyRowService: EmptyRowService,
        public account: AccountPermissionsService
    ) {
        this.api.onLogout.subscribe(() => this.buildings = []);
    }

    getData(scrollToTop?) {
        this.getDataByQueryParams(scrollToTop).subscribe();
    }

    getDataByQueryParams(scrollToTop?) {
        this.showServiceUnavailable = false;
        this.data[this.tabId].total = null;

        const tabId       = this.urlService.queryParams['tab'];
        const url         = this.data[tabId].get;
        const queryParams = this.urlService.getQueryParamsString();

        return this.api.getJson('domainDailyReports', `${url}${queryParams}`).pipe(
            createInitialNetworkEntity(),
            resolveNetworkEntity(
                item => {
                    if (item.building && item.building.bdId) {
                        item.building.id = item.building.bdId;
                    }
                    return DailyReport.fromExistingData(this.account.addPermissionsToEntity(item, this.serviceName));
                }
            ),
            tap(({loading}) => this.spinnerShow = loading),
            tap(
                (body) => {
                    this.data[tabId].data = body.entities;
                    this.data[tabId].total = body.total;
                    this.emptyRowService.fillEmptyRow();
                    if($('.table')[0] && scrollToTop)
                        $('.table')[0].scrollIntoView();
                },
                (error) => {
                    this.showServiceUnavailable = true;
                    this.spinnerShow = false;
                    this.api.errorHandler(error, 'Failed get Daily Reports');
                }
            ),
            filter(({finished}) => finished),
            tap(() => {
                if (this.selectedItemData) {
                    this.selectedItemData = this.data.allDailyReports.data.find(si => si.id == this.selectedItemData.id);
                    if(this.selectedItemData)
                        this.selectedItemData.checked = true;
                    else
                        this.showDetailedView = false;
                }
            })
        );
    }

    getBuildings(): void {
        if (this.buildings.length) return;
        const url = `/v1/buildings/short.json?per-page=10000&order-by=name|asc&expand=timeZone`;
        this.api.get('domainBD', url).subscribe(
            (res) => {
                this.buildings = JSON.parse(res._body).entities;
                this.getFilteredBuildings();
            },
            (error) => this.api.errorHandler(error, 'Failed get buildings')
        );
    }

    resetData() {
        for (const item in this.data) {
            this.data[item].data = [];
            this.data[item].total = null;
        }
    }

    addEntry(dailyReport: DailyReport) {
        this.addEntryModal = true;
        this.dailyReportInDailyReportsModal = Object.assign({}, dailyReport);
    }

    close(dailyReport: DailyReport) {
        if (!dailyReport.id) return;
        swal(ConstantsService.getSwalConfig('Close Daily Report', 'Are you sure you want to close the report?'),
            () => {
                const url = `/v1/daily_report/${dailyReport.id}.json`;
                this.api.patch('domainDailyReports', url, {status: 'closed'}).subscribe(
                    () => {
                        toastr.success('Daily Report has been closed.');
                        this.getData();
                    },
                    (error) => this.api.errorHandler(error, 'Failed get buildings')
                );
            }
        );
    }

    deleteEntry(dailyReport: DailyReport): void {
        let minusPage = 0;
        if (this.data.allDailyReports.total % this.urlService.queryParams['per-page'] == 1)
            minusPage = this.urlService.queryParams['page'] - 1;
        if (!dailyReport.id) return;
        swal(ConstantsService.getSwalConfig('Delete Daily Report'),
            () => {
                const url = `/v1/daily_report/${dailyReport.id}.json`;
                this.api.remove('domainDailyReports', url).subscribe(
                    () => {
                        toastr.success('Daily Report has been deleted.');
                        if (minusPage) {
                            this.urlService.queryParams['page'] = minusPage;
                            this.urlService.setQueryParams('allDailyReports', minusPage);
                        }
                        this.getData();
                    },
                    (error) => this.api.errorHandler(error, 'Delete Daily Report failed')
                );
            }
        );
    }

    viewReport(dailyReport: DailyReport): void {
        this.showDailyEntriesModal = true;
        this.dailyReportInDailyReportsModal = Object.assign({}, dailyReport);
    }

    canEdit(buildingId?: number) {

        return this.account.canUpdate(this.serviceName, buildingId);
    }

    canDelete(buildingId?: number) {

        return this.account.canDelete(this.serviceName, buildingId);
    }

    getFilteredBuildings() {
        this.buildingsForRead = this.account.getBuildingsForRead(this.buildings, this.serviceName);
        this.buildingsForUpdate = this.account.getBuildingsForUpdate(this.buildings, this.serviceName);
    }

    getPDF(report): void {
        const tab = window.open();
        let content = ('<!DOCTYPE html>\n' +
            '<html>\n' +
            '    <head>\n' +
            '        <style>\n' +
            '           pre{' +
            '               font-family: inherit;\n' +
            '                   font-size: inherit;\n' +
            '               margin: 0;' +
            '            }' +
            '            .view-daily-report-header{\n' +
            '                display: block;\n' +
            '                width: 100%;\n' +
            '            }\n' +
            '\n' +
            '            .view-daily-report-row{\n' +
            '                width: 100%;\n' +
            '                display: inline-flex;\n' +
            '            }\n' +
            '            .view-daily-report-header-top{\n' +
            '                width: 50%;\n' +
            '                font-size: 18px;\n' +
            '            }\n' +
            '            .view-daily-report-header-data{\n' +
            '                margin-top: 7px;\n' +
            '                width: 50%;\n' +
            '                font-size: 14px;\n' +
            '            }\n' +
            '            table{\n' +
            '                border-spacing: 0px;\n' +
            '                border-collapse: collapse;\n' +
            '                width: 100%;\n' +
            '                margin-top: 3%;\n' +
            '            }\n' +
            '            th{\n' +
            '                text-align: left;\n' +
            '                padding-left: 10px;\n' +
            '            }\n' +
            '            td{\n' +
            '                border: 1px solid black;\n' +
            '                padding-left: 10px;\n' +
            '            }\n' +
            '        </style>\n' +
            '        <title>Daily Report PDF</title>\n' +
            '        <meta charset="UTF-8">\n' +
            '    </head>\n' +
            '    <body>\n' +
            '        <div style="padding: 10%;">\n' +
            (report.securityCompanyLogo ? `<img src="${report.securityCompanyLogo}" width="150" height="75" style="margin-bottom: 10px;">` : '') +
            '            <div class="view-daily-report-header">\n' +
            '                <div class="view-daily-report-row">\n' +
            `                    <div class="view-daily-report-header-top">Daily Report #: 
                               ${report.dailyReportNumber ? report.dailyReportNumber : '-'}</div>\n` +
            `                    <div class="view-daily-report-header-top">Building Name: 
                                               ${report.buildingName ? report.buildingName : '-'}</div>\n` +
            '                </div>\n' +
            '            </div>\n' +
            '            <div class="view-daily-report-row">\n' +
            `                <div class="view-daily-report-header-top">Start Date: 
                                                         ${new Date(report.startDate).toLocaleDateString()}</div>\n` +
            `                <div class="view-daily-report-header-top">End Date: 
                                                         ${new Date(report.endDate).toLocaleDateString()}</div>\n` +
            '            </div>\n' +
            '            <div class="view-daily-report-row">\n' +
            `                <div class="view-daily-report-header-top">Created On: 
                                                         ${new Date(report.createdAt).toLocaleDateString()}</div>\n` +
            `                <div class="view-daily-report-header-top">Status: 
                                                        ${report.status ? (report.status.charAt(0).toUpperCase() + report.status.slice(1)) : '-'}</div>\n` +
            '            </div>\n' +
            '            <div class="view-daily-report-row" style="margin-top: 2%;">\n' +
            `                <div class="view-daily-report-header-top">Reported By: 
                                                   ${report.reportedBy ? report.reportedBy : '-'}</div>\n` +
            '            </div>\n' +
            '            <div class="view-daily-report-row">\n' +
            `                <div class="view-daily-report-header-top">Relieved: 
                                       ${report.relieved ? report.relieved : '-'}</div>\n` +
            `                <div class="view-daily-report-header-top">Relieved By: 
                                                 ${report.relievedBy ? report.relievedBy : '-'}</div>\n` +
            '            </div>\n' +
            '            <table>\n' +
            '                <tr>\n' +
            '                    <th>Date To</th>\n' +
            '                    <th>Details</th>\n' +
            '                    <th>Created By</th>\n' +
            '                    <th>Created At</th>\n' +
            '                </tr>\n');
        report.entries.forEach(e => {
            content +=
                ('                <tr>\n' +
                    `                    <td>${moment(e.dateTo).format('YYYY-MM-DD')}</td>` +
                    `                    <td>${e.details}</td>` +
                    `                    <td>${e.createdBy ? e.createdBy.fullName : '-'}</td>` +
                    `                    <td>${moment(e.createdAt).format('YYYY-MM-DD hh:mm A')}</td>` +
                    '                </tr>\n');
        });
        content +=
            ('            </table>\n' +
                '        </div>\n' +
                '    </body>\n' +
                '</html>');
        tab.document.write(content);
        tab.stop();
        tab.print();
        tab.close();
    }

    toggleDetailedView(data, state: boolean): void {
        if (!data)
            this.data.allDailyReports.data.forEach(item => item.checked = false);
        this.selectedItemData = data ? data : null;
        this.showDetailedView = state;
        if (this.selectedItemData) {
            this.showDetailedView = true;
            for (const field in this.selectedItemData.formFields)
                this.selectedItemData.formFields[field].editMode = false;
        }
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/daily-reports.service.ts